import { Button, Group, Stack, Table, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  EmptyState,
  ErrorState,
  LoadingSkeleton,
  SectionCard,
  StateBadge,
} from "../../components/ui";
import { preOrderState } from "../../components/ui/statusBadge";
import { api, type PreOrderSummary } from "../../lib/api";
import { Page } from "../../shell/Page";

/**
 * A day order's state, in the words the person who placed it uses. The badge
 * beside it carries the short form from the theme; this is the sentence.
 */
const STATUS_LABELS: Record<string, string> = {
  PENDING: "Awaiting billing",
  PARTIALLY_FULFILLED: "Part fulfilled — balance outstanding",
  CONVERTED: "Fulfilled",
  CANCELLED: "Cancelled",
};

export function PreOrderList() {
  const [preOrders, setPreOrders] = useState<PreOrderSummary[]>();
  const [error, setError] = useState<string>();
  const navigate = useNavigate();

  async function load() {
    try {
      setError(undefined);
      const { preOrders: loaded } = await api.preOrders("mine");
      setPreOrders(loaded);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load day orders.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <Page title="My day orders" subtitle="Most recent first" back="/marketing">
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {!preOrders && !error && <LoadingSkeleton variant="card" count={3} />}

      {preOrders?.length === 0 && (
        <EmptyState
          title="No day orders yet"
          description="What you capture in the field will appear here."
          action={
            <Button onClick={() => navigate("/marketing/pre-order")}>Place one</Button>
          }
        />
      )}

      {preOrders?.map((preOrder) => (
        <SectionCard key={preOrder.id} title={preOrder.reference}>
          <Stack gap="md">
            <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
              <Stack gap={2} style={{ minWidth: 0 }}>
                <Text fz="lg" fw={600} c="gray.9">
                  {preOrder.customer.name}
                </Text>
                {preOrder.customer.phone && (
                  <Text fz="sm" c="gray.6" className="num">
                    {preOrder.customer.phone}
                  </Text>
                )}
                <Text fz="sm" c="gray.6">
                  {new Date(preOrder.createdAt).toLocaleString()} ·{" "}
                  {preOrder.type.replace(/_/g, " ").toLowerCase()}
                </Text>
              </Stack>
              <StateBadge state={preOrderState(preOrder.status) ?? "preOrderPending"} />
            </Group>

            <Text fz="md" c="gray.7">
              {STATUS_LABELS[preOrder.status] ?? preOrder.status}
            </Text>

            {/* A table, because these four numbers per row are compared
                against each other down the column - which is what a table is
                for. It scrolls inside its own box rather than making the page
                scroll sideways, per the brief. */}
            <Table.ScrollContainer minWidth={340}>
              <Table verticalSpacing="xs" horizontalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Product</Table.Th>
                    <Table.Th ta="right">Asked</Table.Th>
                    <Table.Th ta="right">Filled</Table.Th>
                    <Table.Th ta="right">Left</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {preOrder.lineItems.map((line) => (
                    <Table.Tr key={line.productId}>
                      <Table.Td>
                        <Text fz="sm" c="gray.9">
                          {line.productName}
                        </Text>
                        <Text fz="xs" c="gray.6" className="num">
                          {line.sku}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right" className="num">
                        {line.quantityRequested}
                      </Table.Td>
                      <Table.Td ta="right" className="num">
                        {line.quantityFulfilled}
                      </Table.Td>
                      <Table.Td ta="right" className="num">
                        {line.quantityOutstanding > 0 ? (
                          <strong>{line.quantityOutstanding}</strong>
                        ) : (
                          "—"
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            {preOrder.orders.length > 0 && (
              <Text fz="sm" c="gray.6">
                Orders raised:{" "}
                {preOrder.orders.map((o) => `${o.reference} (${o.status})`).join(", ")}
              </Text>
            )}
          </Stack>
        </SectionCard>
      ))}
    </Page>
  );
}
